import debounce from 'lodash/debounce';
import { ChangeEvent, MouseEvent, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { HiOutlineSearch, HiX } from 'react-icons/hi';

type SearchInputProps<T> = {
   className?: string
   preventSubmission?: boolean
   searchValue: string;
   placeholder?: string;
   handleSearch: (value: string) => void;
};

const SearchInput: React.FC<SearchInputProps<any>> = ({ preventSubmission = false, searchValue, placeholder, handleSearch }) => {
   const [t] = useTranslation();
   const inputRef: any = useRef(null);

   const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      handleSearch(e.target.value);
   };

   const debouncedResults = useMemo(() => {
      return debounce(handleChange, 700);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   useEffect(() => {
      return () => {
         debouncedResults.cancel();
      };
   });

   const handleClear = (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault()
      e.stopPropagation()

      handleSearch('');

      inputRef.current.value = '';
   };

   return (
      <div className="relative flex border border-gray-200 bg-white rounded-md min-h-8.5 max-h-8.5 w-full focus-within:ring-2 focus-within:ring-focusRing">
         <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <HiOutlineSearch className="text-gray-500" size={18} color="black" />
         </div>
         <input
            ref={inputRef}
            name="searchField"
            defaultValue={searchValue}
            className="block w-full py-1.25 pl-10 pr-4 text-sm bg-transparent outline-none border-none placeholder-gray-500"
            type="text"
            placeholder={placeholder ?? t('common.search')}
            onChange={debouncedResults}
            onKeyDown={preventSubmission
               ? (e) => {
                  if (e.key === "Enter") {
                     e.preventDefault()

                     handleSearch(e.currentTarget.value)
                  }
               }
               : undefined
            }
         />
         <div className="inline-flex items-center justify-between gap-x-1 mr-3">
            <button
               className={`bg-twGray p-[3px] rounded-full ${searchValue?.trim() !== '' ? 'visible' : 'invisible'}`}
               onClick={handleClear}
            >
               <HiX size={10} color='white' />
            </button>
         </div>
      </div>
   );
};

export default SearchInput;
